import { SalesOrderItem } from '@/renderer/types/order';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from '../auth/authThunks';
import {
  assignClientForSelectedOrder,
  confirmCreateOrderForSelectedTable,
  confirmKotForSelectedOrder,
  confirmPrintReceiptForSettlement,
  createClientForSelectedOrder,
  createTakeAwayOrderForCurrentOutlet,
  exportSalesReportPdfForCurrentOutlet,
  fetchTakeAwayOrdersForOutlet,
  openBillForSelectedOrder,
  openClientModalForSelectedOrder,
  openDiscountForSelectedOrder,
  openNewDayForCurrentOutlet,
  openOrderShiftModalForSelectedOrder,
  openOrderSplitModalForSelectedOrder,
  openSalesReportForCurrentOutlet,
  openSettleModalForSelectedOrder,
  openWaiterModalForSelectedOrder,
  refreshSelectedOrderContext,
  removeSelectedOrderDiscount,
  removeSelectedOrderItem,
  reprintKotForSelectedOrderItem,
  selectDiningTableOrder,
  selectTakeAwayOrderById,
  sendSalesReportEmailForCurrentOutlet,
  shiftSelectedConfirmedOrderItem,
  shiftSelectedConfirmedOrderItems,
  startAddItemFlow,
  submitCreditSettlementForSelectedOrder,
  submitDirectSettlementForSelectedOrder,
  submitOrderShiftForSelectedOrder,
  submitOtherSettlementForSelectedOrder,
  submitSelectedOrderDiscount,
  submitSelectedOrderItem,
  submitSelectedOrderWaiter,
  submitSplitSettlementForSelectedOrder,
  updateSelectedOrderItemNotes,
} from './runningOrderThunks';
import { RunningOrderState } from './runningOrderTypes';

/**
 * Drop the current order/table selection and close every order-related
 * modal. Used whenever a background refresh finds the selected order is no
 * longer OPEN, or can no longer be resolved at all (e.g. deleted by a
 * cross-device conflict resolution) — either way, continuing to show it is
 * worse than clearing it back to the grid.
 */
const clearSelectedOrderSelection = (state: RunningOrderState) => {
  state.selectedTakeAwayId = null;
  state.selectedTable = null;
  state.selectedOrder = null;
  state.selectedOrderItems = [];
  state.selectedOrderItemGroupKey = null;
  state.isOrderItemsModalOpen = false;
  state.isDiscountModalOpen = false;
  state.isSettleModalOpen = false;
  state.isClientModalOpen = false;
  state.isWaiterModalOpen = false;
  state.isLoadingWaiters = false;
  state.isUpdatingOrderWaiter = false;
  state.isOrderShiftModalOpen = false;
  state.isPreparingOrderShift = false;
  state.isShiftingOrder = false;
  state.isShiftingOrderItem = false;
  state.isKotNumberModalOpen = false;
  state.outletWaiters = [];
};

const initialState: RunningOrderState = {
  openDiningSectionId: null,
  selectedTable: null,
  selectedTakeAwayId: null,
  takeAwayOrders: [],
  selectedOrder: null,
  selectedOrderItems: [],
  selectedOrderItemGroupKey: null,
  isOrderItemsModalOpen: false,
  isOrderLoading: false,
  isTakeAwayOrdersLoading: false,
  createOrderTargetTable: null,
  isCreateOrderModalOpen: false,
  isCreatingOrder: false,
  isCreatingTakeAwayOrder: false,
  menuItemForModal: null,
  isAddItemModalOpen: false,
  isAddingOrderItem: false,
  isDiscountModalOpen: false,
  isPreparingDiscount: false,
  isApplyingDiscount: false,
  isRemovingDiscount: false,
  isPreparingBill: false,
  isConfirmingKot: false,
  isKotNumberModalOpen: false,
  isReprintingKot: false,
  isSettleModalOpen: false,
  isLoadingSettleOptions: false,
  isSubmittingSettlement: false,
  isClientModalOpen: false,
  isLoadingClientModalData: false,
  isAssigningClientToOrder: false,
  isCreatingClient: false,
  isWaiterModalOpen: false,
  isLoadingWaiters: false,
  isUpdatingOrderWaiter: false,
  isOrderShiftModalOpen: false,
  isPreparingOrderShift: false,
  isShiftingOrder: false,
  isShiftingOrderItem: false,
  isQuickActionsModalOpen: false,
  isOpenNewDayModalOpen: false,
  isSalesReportModalOpen: false,
  isLoadingSalesReport: false,
  isOpeningNewDay: false,
  isExportingSalesReport: false,
  isSendingReportEmail: false,
  openNewDayTargetDate: null,
  isUpdatingOrderItemNotes: false,
  isRemovingOrderItem: false,
  settlementPaymentMethods: [],
  settlementClients: [],
  companyClients: [],
  outletWaiters: [],
  salesReport: null,
  error: null,
  activeTableSelectionRequestId: null,
  isPrintReceiptPromptOpen: false,
  pendingReceiptContext: null,
  isPrintingReceipt: false,
  // Split order
  isPreparingOrderSplit: false,
  isOrderSplitModalOpen: false,
};

const runningOrderSlice = createSlice({
  name: 'runningOrder',
  initialState,
  reducers: {
    clearRunningOrderError: (state) => {
      state.error = null;
    },
    setOpenDiningSection: (state, action: PayloadAction<number>) => {
      state.openDiningSectionId = action.payload;
    },
    openOrderItemsModalForGroup: (state, action: PayloadAction<string>) => {
      state.selectedOrderItemGroupKey = action.payload;
      state.isOrderItemsModalOpen = true;
    },
    closeOrderItemsModal: (state) => {
      state.selectedOrderItemGroupKey = null;
      state.isOrderItemsModalOpen = false;
    },
    selectTakeAwayOrder: (state, action: PayloadAction<string>) => {
      state.selectedTakeAwayId = action.payload;
      state.selectedTable = null;
      state.selectedOrder = null;
      state.selectedOrderItems = [];
      state.selectedOrderItemGroupKey = null;
      state.isOrderItemsModalOpen = false;
      state.isOrderLoading = false;
      state.createOrderTargetTable = null;
      state.isCreateOrderModalOpen = false;
      state.isCreatingOrder = false;
      state.menuItemForModal = null;
      state.isAddItemModalOpen = false;
      state.isAddingOrderItem = false;
      state.isDiscountModalOpen = false;
      state.isPreparingDiscount = false;
      state.isApplyingDiscount = false;
      state.isRemovingDiscount = false;
      state.isPreparingBill = false;
      state.isConfirmingKot = false;
      state.isKotNumberModalOpen = false;
      state.isSettleModalOpen = false;
      state.isLoadingSettleOptions = false;
      state.isSubmittingSettlement = false;
      state.isClientModalOpen = false;
      state.isLoadingClientModalData = false;
      state.isAssigningClientToOrder = false;
      state.isCreatingClient = false;
      state.isWaiterModalOpen = false;
      state.isLoadingWaiters = false;
      state.isUpdatingOrderWaiter = false;
      state.isOrderShiftModalOpen = false;
      state.isPreparingOrderShift = false;
      state.isShiftingOrder = false;
      state.isShiftingOrderItem = false;
      state.outletWaiters = [];
      state.settlementPaymentMethods = [];
      state.settlementClients = [];
      state.companyClients = [];
      state.isUpdatingOrderItemNotes = false;
      state.isRemovingOrderItem = false;
      state.isPrintReceiptPromptOpen = false;
      state.pendingReceiptContext = null;
      state.isPrintingReceipt = false;
      state.error = null;
    },
    closeCreateOrderModal: (state) => {
      state.isCreateOrderModalOpen = false;
      state.createOrderTargetTable = null;
      state.isCreatingOrder = false;
    },
    closeAddItemModal: (state) => {
      state.isAddItemModalOpen = false;
      state.menuItemForModal = null;
      state.isAddingOrderItem = false;
    },
    closeDiscountModal: (state) => {
      state.isDiscountModalOpen = false;
      state.isPreparingDiscount = false;
      state.isApplyingDiscount = false;
      state.isRemovingDiscount = false;
    },
    openKotNumberModal: (state) => {
      state.isKotNumberModalOpen = true;
    },
    closeKotNumberModal: (state) => {
      state.isKotNumberModalOpen = false;
    },
    closeSettleModal: (state) => {
      state.isSettleModalOpen = false;
      state.isSubmittingSettlement = false;
      state.settlementPaymentMethods = [];
      state.settlementClients = [];
    },
    closePrintReceiptPrompt: (state) => {
      state.isPrintReceiptPromptOpen = false;
      state.pendingReceiptContext = null;
    },
    closeClientModal: (state) => {
      state.isClientModalOpen = false;
      state.isLoadingClientModalData = false;
      state.isAssigningClientToOrder = false;
      state.isCreatingClient = false;
    },
    closeWaiterModal: (state) => {
      state.isWaiterModalOpen = false;
      state.isLoadingWaiters = false;
      state.isUpdatingOrderWaiter = false;
      state.outletWaiters = [];
    },
    closeOrderShiftModal: (state) => {
      state.isOrderShiftModalOpen = false;
      state.isPreparingOrderShift = false;
      state.isShiftingOrder = false;
    },
    closeOrderSplitModal: (state) => {
      state.isOrderSplitModalOpen = false;
      state.isPreparingOrderSplit = false;
      state.isShiftingOrder = false;
    },
    openQuickActionsModal: (state) => {
      state.isQuickActionsModalOpen = true;
    },
    openOpenNewDayModal: (state, action: PayloadAction<string>) => {
      state.isQuickActionsModalOpen = false;
      state.isOpenNewDayModalOpen = true;
      state.openNewDayTargetDate = action.payload;
    },
    setOpenNewDayTargetDate: (state, action: PayloadAction<string>) => {
      state.openNewDayTargetDate = action.payload;
    },
    closeOpenNewDayModal: (state) => {
      state.isOpenNewDayModalOpen = false;
      state.isOpeningNewDay = false;
      state.openNewDayTargetDate = null;
    },
    closeQuickActionsModal: (state) => {
      state.isQuickActionsModalOpen = false;
      state.isOpeningNewDay = false;
      state.isLoadingSalesReport = false;
    },
    closeSalesReportModal: (state) => {
      state.isSalesReportModalOpen = false;
      state.isExportingSalesReport = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(selectDiningTableOrder.pending, (state, action) => {
        state.activeTableSelectionRequestId = action.meta.requestId;
        state.error = null;
        state.isOrderLoading = true;
        state.selectedTakeAwayId = null;
        state.selectedTable = action.meta.arg;
        state.openDiningSectionId = action.meta.arg.sectionId;
        state.selectedOrder = null;
        state.selectedOrderItems = [];
        state.selectedOrderItemGroupKey = null;
        state.isOrderItemsModalOpen = false;
        state.createOrderTargetTable = null;
        state.isCreateOrderModalOpen = false;
        state.isCreatingOrder = false;
        state.menuItemForModal = null;
        state.isAddItemModalOpen = false;
        state.isAddingOrderItem = false;
        state.isDiscountModalOpen = false;
        state.isPreparingDiscount = false;
        state.isApplyingDiscount = false;
        state.isRemovingDiscount = false;
        state.isPreparingBill = false;
        state.isConfirmingKot = false;
        state.isKotNumberModalOpen = false;
        state.isReprintingKot = false;
        state.isSettleModalOpen = false;
        state.isLoadingSettleOptions = false;
        state.isSubmittingSettlement = false;
        state.isClientModalOpen = false;
        state.isLoadingClientModalData = false;
        state.isAssigningClientToOrder = false;
        state.isCreatingClient = false;
        state.isWaiterModalOpen = false;
        state.isLoadingWaiters = false;
        state.isUpdatingOrderWaiter = false;
        state.isOrderShiftModalOpen = false;
        state.isPreparingOrderShift = false;
        state.isShiftingOrder = false;
        state.isShiftingOrderItem = false;
        state.outletWaiters = [];
        state.settlementPaymentMethods = [];
        state.settlementClients = [];
        state.companyClients = [];
        state.isUpdatingOrderItemNotes = false;
        state.isRemovingOrderItem = false;
        state.isPrintReceiptPromptOpen = false;
        state.pendingReceiptContext = null;
        state.isPrintingReceipt = false;
      })
      .addCase(selectDiningTableOrder.fulfilled, (state, action) => {
        if (state.activeTableSelectionRequestId !== action.meta.requestId) {
          return;
        }

        state.isOrderLoading = false;
        if (action.payload.kind === 'found') {
          state.selectedOrder = action.payload.order;
          state.selectedOrderItems = action.payload.items;
          state.isCreateOrderModalOpen = false;
          state.createOrderTargetTable = null;
          return;
        }

        state.selectedOrder = null;
        state.selectedOrderItems = [];
        state.createOrderTargetTable = action.payload.table;
        state.isCreateOrderModalOpen = true;
      })
      .addCase(selectDiningTableOrder.rejected, (state, action) => {
        if (state.activeTableSelectionRequestId !== action.meta.requestId) {
          return;
        }

        state.isOrderLoading = false;
        state.selectedOrder = null;
        state.selectedOrderItems = [];
        state.selectedOrderItemGroupKey = null;
        state.isOrderItemsModalOpen = false;
        state.error = action.payload || 'Failed to fetch selected table order';
      })
      .addCase(fetchTakeAwayOrdersForOutlet.pending, (state) => {
        state.error = null;
        state.isTakeAwayOrdersLoading = true;
      })
      .addCase(fetchTakeAwayOrdersForOutlet.fulfilled, (state, action) => {
        state.isTakeAwayOrdersLoading = false;
        state.takeAwayOrders = action.payload;
      })
      .addCase(fetchTakeAwayOrdersForOutlet.rejected, (state, action) => {
        state.isTakeAwayOrdersLoading = false;
        state.error = action.payload || 'Failed to fetch takeaway orders';
      })
      .addCase(openSalesReportForCurrentOutlet.pending, (state) => {
        state.error = null;
        state.isLoadingSalesReport = true;
      })
      .addCase(openSalesReportForCurrentOutlet.fulfilled, (state, action) => {
        state.isLoadingSalesReport = false;
        state.isQuickActionsModalOpen = false;
        state.isSalesReportModalOpen = true;
        state.salesReport = action.payload;
      })
      .addCase(openSalesReportForCurrentOutlet.rejected, (state, action) => {
        state.isLoadingSalesReport = false;
        state.error = action.payload || 'Failed to load sales report';
      })
      .addCase(exportSalesReportPdfForCurrentOutlet.pending, (state) => {
        state.error = null;
        state.isExportingSalesReport = true;
      })
      .addCase(exportSalesReportPdfForCurrentOutlet.fulfilled, (state) => {
        state.isExportingSalesReport = false;
      })
      .addCase(
        exportSalesReportPdfForCurrentOutlet.rejected,
        (state, action) => {
          state.isExportingSalesReport = false;
          if (action.payload === 'Export cancelled') {
            return;
          }
          state.error = action.payload || 'Failed to export sales report';
        },
      )
      .addCase(sendSalesReportEmailForCurrentOutlet.pending, (state) => {
        state.error = null;
        state.isSendingReportEmail = true;
      })
      .addCase(sendSalesReportEmailForCurrentOutlet.fulfilled, (state) => {
        state.isSendingReportEmail = false;
      })
      .addCase(
        sendSalesReportEmailForCurrentOutlet.rejected,
        (state, action) => {
          state.isSendingReportEmail = false;
          state.error = action.payload || 'Failed to send sales report email';
        },
      )
      .addCase(openNewDayForCurrentOutlet.pending, (state) => {
        state.error = null;
        state.isOpeningNewDay = true;
      })
      .addCase(openNewDayForCurrentOutlet.fulfilled, (state) => {
        state.isOpeningNewDay = false;
        state.isQuickActionsModalOpen = false;
        state.isOpenNewDayModalOpen = false;
        state.isSalesReportModalOpen = false;
        state.isExportingSalesReport = false;
        state.openNewDayTargetDate = null;
        state.salesReport = null;
      })
      .addCase(openNewDayForCurrentOutlet.rejected, (state, action) => {
        state.isOpeningNewDay = false;
        if (action.payload === 'The current business day is already open') {
          return;
        }
        state.error = action.payload || 'Failed to open new day';
      })
      .addCase(selectTakeAwayOrderById.pending, (state, action) => {
        state.error = null;
        state.isOrderLoading = true;
        state.selectedTakeAwayId = action.meta.arg;
        state.selectedTable = null;
        state.selectedOrder = null;
        state.selectedOrderItems = [];
        state.isWaiterModalOpen = false;
        state.isLoadingWaiters = false;
        state.isUpdatingOrderWaiter = false;
        state.isOrderShiftModalOpen = false;
        state.isPreparingOrderShift = false;
        state.isShiftingOrder = false;
        state.isShiftingOrderItem = false;
        state.outletWaiters = [];
      })
      .addCase(selectTakeAwayOrderById.fulfilled, (state, action) => {
        state.isOrderLoading = false;
        state.selectedTakeAwayId = action.payload.orderId;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(selectTakeAwayOrderById.rejected, (state, action) => {
        state.isOrderLoading = false;
        state.selectedOrder = null;
        state.selectedOrderItems = [];
        state.error = action.payload || 'Failed to select takeaway order';
      })
      .addCase(createTakeAwayOrderForCurrentOutlet.pending, (state) => {
        state.error = null;
        state.isCreatingTakeAwayOrder = true;
      })
      .addCase(
        createTakeAwayOrderForCurrentOutlet.fulfilled,
        (state, action) => {
          state.isCreatingTakeAwayOrder = false;
          state.selectedTakeAwayId = action.payload.order.order_id;
          state.selectedTable = null;
          state.selectedOrder = action.payload.order;
          state.selectedOrderItems = action.payload.items;
          state.takeAwayOrders = action.payload.takeAwayOrders;
        },
      )
      .addCase(
        createTakeAwayOrderForCurrentOutlet.rejected,
        (state, action) => {
          state.isCreatingTakeAwayOrder = false;
          state.error = action.payload || 'Failed to create takeaway order';
        },
      )
      .addCase(confirmCreateOrderForSelectedTable.pending, (state) => {
        state.error = null;
        state.isCreatingOrder = true;
      })
      .addCase(
        confirmCreateOrderForSelectedTable.fulfilled,
        (state, action) => {
          state.isCreatingOrder = false;
          state.isCreateOrderModalOpen = false;
          state.createOrderTargetTable = null;
          state.selectedOrder = action.payload.order;
          state.selectedOrderItems = action.payload.items;
        },
      )
      .addCase(confirmCreateOrderForSelectedTable.rejected, (state, action) => {
        state.isCreatingOrder = false;
        state.error = action.payload || 'Failed to create order';
      })
      .addCase(startAddItemFlow.fulfilled, (state, action) => {
        state.error = null;
        state.menuItemForModal = action.payload;
        state.isAddItemModalOpen = true;
      })
      .addCase(startAddItemFlow.rejected, (state) => {
        state.menuItemForModal = null;
        state.isAddItemModalOpen = false;
      })
      .addCase(submitSelectedOrderItem.pending, (state, action) => {
        state.error = null;
        // Modal closes immediately — isAddingOrderItem stays false so the next
        // modal open is not blocked while the background API call completes.
        // Close the modal immediately so the user can queue more items
        state.isAddItemModalOpen = false;
        state.isOrderItemsModalOpen = false;
        // Inject an optimistic item so it appears in the list before the API responds
        const menuItem = state.menuItemForModal;
        const order = state.selectedOrder;
        if (menuItem && order) {
          const { quantity, notes } = action.meta.arg as {
            quantity: number;
            notes?: string;
          };
          const optimisticItem: SalesOrderItem = {
            id: `OPTIMISTIC_${action.meta.requestId}`,
            order_id: order.order_id,
            menu_item_id: menuItem.id,
            name: menuItem.name,
            department_id:
              (menuItem as { department_id?: string | null }).department_id ??
              null,
            department_name:
              (menuItem as { department_name?: string | null })
                .department_name ?? null,
            quantity,
            unit_price: menuItem.base_price,
            amount: quantity * menuItem.base_price,
            notes: notes ?? null,
            status: 'PENDING',
            kot_id: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          state.selectedOrderItems = [
            ...state.selectedOrderItems,
            optimisticItem,
          ];
        }
        // Do NOT clear menuItemForModal here — the thunk reads it via getState()
        // after this reducer runs. It is cleared on fulfilled/rejected instead.
      })
      .addCase(submitSelectedOrderItem.fulfilled, (state, action) => {
        state.isAddingOrderItem = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
        state.selectedOrderItemGroupKey = null;
        state.isOrderItemsModalOpen = false;
        state.isAddItemModalOpen = false;
        state.menuItemForModal = null;
      })
      .addCase(submitSelectedOrderItem.rejected, (state, action) => {
        state.isAddingOrderItem = false;
        state.error = action.payload || 'Failed to add item to order';
        state.menuItemForModal = null;
        // Remove only this thunk's optimistic item — concurrent adds are unaffected
        const optimisticId = `OPTIMISTIC_${action.meta.requestId}`;
        state.selectedOrderItems = state.selectedOrderItems.filter(
          (item) => item.id !== optimisticId,
        );
      })
      .addCase(openDiscountForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isPreparingDiscount = true;
      })
      .addCase(openDiscountForSelectedOrder.fulfilled, (state, action) => {
        state.isPreparingDiscount = false;
        state.error = null;
        state.selectedOrder = action.payload;
        state.isDiscountModalOpen = true;
      })
      .addCase(openDiscountForSelectedOrder.rejected, (state, action) => {
        state.isPreparingDiscount = false;
        state.error = action.payload || null;
      })
      .addCase(submitSelectedOrderDiscount.pending, (state) => {
        state.error = null;
        state.isApplyingDiscount = true;
      })
      .addCase(submitSelectedOrderDiscount.fulfilled, (state, action) => {
        state.isApplyingDiscount = false;
        state.isDiscountModalOpen = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
        state.selectedOrderItemGroupKey = null;
        state.isOrderItemsModalOpen = false;
      })
      .addCase(submitSelectedOrderDiscount.rejected, (state, action) => {
        state.isApplyingDiscount = false;
        state.error = action.payload || 'Failed to apply discount';
      })
      .addCase(removeSelectedOrderDiscount.pending, (state) => {
        state.error = null;
        state.isRemovingDiscount = true;
      })
      .addCase(removeSelectedOrderDiscount.fulfilled, (state, action) => {
        state.isRemovingDiscount = false;
        state.isDiscountModalOpen = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(removeSelectedOrderDiscount.rejected, (state, action) => {
        state.isRemovingDiscount = false;
        state.error = action.payload || 'Failed to remove discount';
      })
      .addCase(openBillForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isPreparingBill = true;
      })
      .addCase(openBillForSelectedOrder.fulfilled, (state, action) => {
        state.isPreparingBill = false;
        state.selectedOrder = action.payload;
      })
      .addCase(openBillForSelectedOrder.rejected, (state, action) => {
        state.isPreparingBill = false;
        state.error = action.payload || 'Failed to prepare bill';
      })
      .addCase(confirmKotForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isConfirmingKot = true;
      })
      .addCase(confirmKotForSelectedOrder.fulfilled, (state, action) => {
        state.isConfirmingKot = false;
        state.isKotNumberModalOpen = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(confirmKotForSelectedOrder.rejected, (state, action) => {
        state.isConfirmingKot = false;
        state.error = action.payload || 'Failed to confirm order items';
      })
      .addCase(reprintKotForSelectedOrderItem.pending, (state) => {
        state.error = null;
        state.isReprintingKot = true;
      })
      .addCase(reprintKotForSelectedOrderItem.fulfilled, (state) => {
        state.isReprintingKot = false;
      })
      .addCase(reprintKotForSelectedOrderItem.rejected, (state, action) => {
        state.isReprintingKot = false;
        state.error = action.payload || 'Failed to re-print KOT';
      })
      .addCase(openSettleModalForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isLoadingSettleOptions = true;
        state.settlementPaymentMethods = [];
        state.settlementClients = [];
      })
      .addCase(openSettleModalForSelectedOrder.fulfilled, (state, action) => {
        state.isLoadingSettleOptions = false;
        state.selectedOrder = action.payload.order;
        state.settlementPaymentMethods = action.payload.paymentMethods;
        state.settlementClients = action.payload.clients;
        state.isSettleModalOpen = true;
      })
      .addCase(openSettleModalForSelectedOrder.rejected, (state, action) => {
        state.isLoadingSettleOptions = false;
        state.settlementPaymentMethods = [];
        state.settlementClients = [];
        state.error = action.payload || 'Failed to prepare settlement';
      })
      .addCase(openWaiterModalForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isLoadingWaiters = true;
        state.outletWaiters = [];
      })
      .addCase(openWaiterModalForSelectedOrder.fulfilled, (state, action) => {
        state.isLoadingWaiters = false;
        state.selectedOrder = action.payload.order;
        state.outletWaiters = action.payload.waiters;
        state.isWaiterModalOpen = true;
      })
      .addCase(openWaiterModalForSelectedOrder.rejected, (state, action) => {
        state.isLoadingWaiters = false;
        state.outletWaiters = [];
        state.error = action.payload || 'Failed to load waiters';
      })
      .addCase(submitSelectedOrderWaiter.pending, (state) => {
        state.error = null;
        state.isUpdatingOrderWaiter = true;
      })
      .addCase(submitSelectedOrderWaiter.fulfilled, (state, action) => {
        state.isUpdatingOrderWaiter = false;
        state.isWaiterModalOpen = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(submitSelectedOrderWaiter.rejected, (state, action) => {
        state.isUpdatingOrderWaiter = false;
        state.error = action.payload || 'Failed to update order waiter';
      })
      .addCase(openOrderShiftModalForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isPreparingOrderShift = true;
        state.isOrderShiftModalOpen = false;
      })
      .addCase(
        openOrderShiftModalForSelectedOrder.fulfilled,
        (state, action) => {
          state.isPreparingOrderShift = false;
          state.selectedOrder = action.payload;
          state.isOrderShiftModalOpen = true;
        },
      )
      .addCase(
        openOrderShiftModalForSelectedOrder.rejected,
        (state, action) => {
          state.isPreparingOrderShift = false;
          state.isOrderShiftModalOpen = false;
          state.error = action.payload || 'Failed to open shift order modal';
        },
      )
      // Split items
      .addCase(openOrderSplitModalForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isPreparingOrderSplit = true;
        state.isOrderSplitModalOpen = false;
      })
      .addCase(
        openOrderSplitModalForSelectedOrder.fulfilled,
        (state, action) => {
          state.isPreparingOrderSplit = false;
          state.selectedOrder = action.payload;
          state.isOrderSplitModalOpen = true;
        },
      )
      .addCase(
        openOrderSplitModalForSelectedOrder.rejected,
        (state, action) => {
          state.isPreparingOrderSplit = false;
          state.isOrderSplitModalOpen = false;
          state.error = action.payload || 'Failed to open split order modal';
        },
      )
      .addCase(submitOrderShiftForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isShiftingOrder = true;
      })
      .addCase(submitOrderShiftForSelectedOrder.fulfilled, (state, action) => {
        state.isShiftingOrder = false;
        state.isOrderShiftModalOpen = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;

        if (
          action.payload.order.mode === 'DINE' &&
          action.payload.order.table_id
        ) {
          state.selectedTable = {
            id: action.payload.order.table_id,
            name: action.payload.order.table_name,
            sectionId: action.payload.order.section_id,
            sectionName: action.payload.order.section_name,
          };
        }
      })
      .addCase(submitOrderShiftForSelectedOrder.rejected, (state, action) => {
        state.isShiftingOrder = false;
        state.error = action.payload || 'Failed to shift order';
      })
      .addCase(shiftSelectedConfirmedOrderItem.pending, (state) => {
        state.error = null;
        state.isShiftingOrderItem = true;
      })
      .addCase(shiftSelectedConfirmedOrderItem.fulfilled, (state, action) => {
        state.isShiftingOrderItem = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(shiftSelectedConfirmedOrderItem.rejected, (state, action) => {
        state.isShiftingOrderItem = false;
        state.error = action.payload || 'Failed to shift order item';
      })
      .addCase(shiftSelectedConfirmedOrderItems.pending, (state) => {
        state.error = null;
        state.isShiftingOrderItem = true;
      })
      .addCase(shiftSelectedConfirmedOrderItems.fulfilled, (state, action) => {
        state.isShiftingOrderItem = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(shiftSelectedConfirmedOrderItems.rejected, (state, action) => {
        state.isShiftingOrderItem = false;
        state.error = action.payload || 'Failed to shift order items';
      })
      .addCase(openClientModalForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isLoadingClientModalData = true;
        state.companyClients = [];
      })
      .addCase(openClientModalForSelectedOrder.fulfilled, (state, action) => {
        state.isLoadingClientModalData = false;
        state.selectedOrder = action.payload.order;
        state.companyClients = action.payload.clients;
        state.isClientModalOpen = true;
      })
      .addCase(openClientModalForSelectedOrder.rejected, (state, action) => {
        state.isLoadingClientModalData = false;
        state.error = action.payload || 'Failed to load client data';
      })
      .addCase(assignClientForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isAssigningClientToOrder = true;
      })
      .addCase(assignClientForSelectedOrder.fulfilled, (state, action) => {
        state.isAssigningClientToOrder = false;
        state.isClientModalOpen = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
        state.companyClients = action.payload.clients;
      })
      .addCase(assignClientForSelectedOrder.rejected, (state, action) => {
        state.isAssigningClientToOrder = false;
        state.error = action.payload || 'Failed to add client to order';
      })
      .addCase(createClientForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isCreatingClient = true;
      })
      .addCase(createClientForSelectedOrder.fulfilled, (state, action) => {
        state.isCreatingClient = false;
        state.isClientModalOpen = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
        state.companyClients = action.payload.clients;
      })
      .addCase(createClientForSelectedOrder.rejected, (state, action) => {
        state.isCreatingClient = false;
        state.error = action.payload || 'Failed to create client';
      })
      .addCase(submitDirectSettlementForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isSubmittingSettlement = true;
      })
      .addCase(
        submitDirectSettlementForSelectedOrder.fulfilled,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.isSettleModalOpen = false;
          state.selectedTakeAwayId = null;
          state.selectedTable = null;
          state.selectedOrder = null;
          state.selectedOrderItems = [];
          state.selectedOrderItemGroupKey = null;
          state.isOrderItemsModalOpen = false;
          state.isWaiterModalOpen = false;
          state.isLoadingWaiters = false;
          state.isUpdatingOrderWaiter = false;
          state.isOrderShiftModalOpen = false;
          state.isPreparingOrderShift = false;
          state.isShiftingOrder = false;
          state.isShiftingOrderItem = false;
          state.outletWaiters = [];
          state.pendingReceiptContext = action.payload;
          state.isPrintReceiptPromptOpen = true;
        },
      )
      .addCase(
        submitDirectSettlementForSelectedOrder.rejected,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.error = action.payload || 'Failed to settle order';
        },
      )
      .addCase(submitSplitSettlementForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isSubmittingSettlement = true;
      })
      .addCase(
        submitSplitSettlementForSelectedOrder.fulfilled,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.isSettleModalOpen = false;
          state.selectedTakeAwayId = null;
          state.selectedTable = null;
          state.selectedOrder = null;
          state.selectedOrderItems = [];
          state.selectedOrderItemGroupKey = null;
          state.isOrderItemsModalOpen = false;
          state.isWaiterModalOpen = false;
          state.isLoadingWaiters = false;
          state.isUpdatingOrderWaiter = false;
          state.isOrderShiftModalOpen = false;
          state.isPreparingOrderShift = false;
          state.isShiftingOrder = false;
          state.isShiftingOrderItem = false;
          state.outletWaiters = [];
          state.pendingReceiptContext = action.payload;
          state.isPrintReceiptPromptOpen = true;
        },
      )
      .addCase(
        submitSplitSettlementForSelectedOrder.rejected,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.error = action.payload || 'Failed to settle order';
        },
      )
      .addCase(submitCreditSettlementForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isSubmittingSettlement = true;
      })
      .addCase(
        submitCreditSettlementForSelectedOrder.fulfilled,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.isSettleModalOpen = false;
          state.selectedTakeAwayId = null;
          state.selectedTable = null;
          state.selectedOrder = null;
          state.selectedOrderItems = [];
          state.selectedOrderItemGroupKey = null;
          state.isOrderItemsModalOpen = false;
          state.isWaiterModalOpen = false;
          state.isLoadingWaiters = false;
          state.isUpdatingOrderWaiter = false;
          state.isOrderShiftModalOpen = false;
          state.isPreparingOrderShift = false;
          state.isShiftingOrder = false;
          state.isShiftingOrderItem = false;
          state.outletWaiters = [];
          state.pendingReceiptContext = action.payload;
          state.isPrintReceiptPromptOpen = true;
        },
      )
      .addCase(
        submitCreditSettlementForSelectedOrder.rejected,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.error = action.payload || 'Failed to settle order';
        },
      )
      .addCase(submitOtherSettlementForSelectedOrder.pending, (state) => {
        state.error = null;
        state.isSubmittingSettlement = true;
      })
      .addCase(
        submitOtherSettlementForSelectedOrder.fulfilled,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.isSettleModalOpen = false;
          state.selectedTakeAwayId = null;
          state.selectedTable = null;
          state.selectedOrder = null;
          state.selectedOrderItems = [];
          state.selectedOrderItemGroupKey = null;
          state.isOrderItemsModalOpen = false;
          state.isWaiterModalOpen = false;
          state.isLoadingWaiters = false;
          state.isUpdatingOrderWaiter = false;
          state.isOrderShiftModalOpen = false;
          state.isPreparingOrderShift = false;
          state.isShiftingOrder = false;
          state.isShiftingOrderItem = false;
          state.outletWaiters = [];
          state.pendingReceiptContext = action.payload;
          state.isPrintReceiptPromptOpen = true;
        },
      )
      .addCase(
        submitOtherSettlementForSelectedOrder.rejected,
        (state, action) => {
          state.isSubmittingSettlement = false;
          state.error = action.payload || 'Failed to settle order';
        },
      )
      .addCase(confirmPrintReceiptForSettlement.pending, (state) => {
        state.error = null;
        state.isPrintingReceipt = true;
      })
      .addCase(confirmPrintReceiptForSettlement.fulfilled, (state) => {
        state.isPrintingReceipt = false;
        state.isPrintReceiptPromptOpen = false;
        state.pendingReceiptContext = null;
      })
      .addCase(confirmPrintReceiptForSettlement.rejected, (state, action) => {
        state.isPrintingReceipt = false;
        state.isPrintReceiptPromptOpen = false;
        state.pendingReceiptContext = null;
        state.error = action.payload || 'Failed to print receipt';
      })
      .addCase(logout.fulfilled, (state) => {
        state.isSubmittingSettlement = false;
        state.isSettleModalOpen = false;
        state.selectedTakeAwayId = null;
        state.selectedOrder = null;
        state.selectedOrderItems = [];
        state.selectedOrderItemGroupKey = null;
        state.isOrderItemsModalOpen = false;
        state.isWaiterModalOpen = false;
        state.isLoadingWaiters = false;
        state.isUpdatingOrderWaiter = false;
        state.isOrderShiftModalOpen = false;
        state.isPreparingOrderShift = false;
        state.isShiftingOrder = false;
        state.isShiftingOrderItem = false;
        state.isPrintReceiptPromptOpen = false;
        state.pendingReceiptContext = null;
        state.isPrintingReceipt = false;
        state.outletWaiters = [];
      })
      .addCase(refreshSelectedOrderContext.pending, (state) => {
        state.error = null;
      })
      .addCase(refreshSelectedOrderContext.fulfilled, (state, action) => {
        if (action.payload.order.status_name !== 'OPEN') {
          clearSelectedOrderSelection(state);
          return;
        }

        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(refreshSelectedOrderContext.rejected, (state, action) => {
        if (action.payload === 'No selected order') return;
        // The order could not be resolved at all (e.g. deleted by a
        // cross-device conflict resolution) — drop the stale selection
        // instead of leaving a ghost order on screen that only surfaces
        // "Order not found" on the user's next click.
        state.error = action.payload || 'Failed to refresh selected order';
        clearSelectedOrderSelection(state);
      })
      .addCase(updateSelectedOrderItemNotes.pending, (state) => {
        state.error = null;
        state.isUpdatingOrderItemNotes = true;
      })
      .addCase(updateSelectedOrderItemNotes.fulfilled, (state, action) => {
        state.isUpdatingOrderItemNotes = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(updateSelectedOrderItemNotes.rejected, (state, action) => {
        state.isUpdatingOrderItemNotes = false;
        state.error = action.payload || 'Failed to update order item notes';
      })
      .addCase(removeSelectedOrderItem.pending, (state) => {
        state.error = null;
        state.isRemovingOrderItem = true;
      })
      .addCase(removeSelectedOrderItem.fulfilled, (state, action) => {
        state.isRemovingOrderItem = false;
        state.selectedOrder = action.payload.order;
        state.selectedOrderItems = action.payload.items;
      })
      .addCase(removeSelectedOrderItem.rejected, (state, action) => {
        state.isRemovingOrderItem = false;
        state.error = action.payload || 'Failed to remove order item';
      });
  },
});

export const {
  clearRunningOrderError,
  closeAddItemModal,
  closeClientModal,
  closeCreateOrderModal,
  closeDiscountModal,
  closeKotNumberModal,
  closeOpenNewDayModal,
  closeQuickActionsModal,
  closeOrderItemsModal,
  closePrintReceiptPrompt,
  closeSalesReportModal,
  closeSettleModal,
  closeWaiterModal,
  closeOrderShiftModal,
  openKotNumberModal,
  openOpenNewDayModal,
  openQuickActionsModal,
  openOrderItemsModalForGroup,
  setOpenNewDayTargetDate,
  selectTakeAwayOrder,
  setOpenDiningSection,
  closeOrderSplitModal,
} = runningOrderSlice.actions;

export default runningOrderSlice.reducer;
